import React from "react";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Link } from "react-router-dom"
import { Phone, Mail, MapPin, Heart, Award, Users, Clock, ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import BHAVYADASARA from "../images/DR.BHAVYA DASARA.JPG";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"

export default function NephrologyPage() {
  const { open: openAppointment } = useAppointment();
  const doctors = useDepartmentDoctors("Nephrology");


  const subspecialties = [
    {
      title: "Chronic Kidney Disease (CKD) Management",
      description: "Comprehensive care for long-term kidney disease with personalized treatment plans to slow progression.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Chronic-Kidney-Disease-CKD-Management-scaled.jpg",
    },
    {
      title: "Dialysis Services",
      description: "24/7 hemodialysis and peritoneal dialysis services with state-of-the-art equipment.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Dialysis-Services-scaled.jpg",
    },
    {
      title: "Kidney Transplant Program",
      description: "Complete pre-transplant evaluation and post-transplant care.",
      image: "/KidneyTransplantProgram.jpg",
    },
    {
      title: "Hypertension & Diabetic Nephropathy Care",
      description: "Management of kidney complications from diabetes and hypertension.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Hypertension-Diabetic-Nephropathy-Care-scaled.jpg",
    },
    {
      title: "Acute Kidney Injury (AKI) Management",
      description: "Emergency treatment for sudden kidney failure with intensive monitoring.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Acute-Kidney-Injury-AKI-Management-scaled.jpg",
    },
    {
      title: "Pediatric Care",
      description: "Kidney care for children with congenital or acquired kidney conditions.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Kidney-care-for-children-scaled.jpg",
    },
  ];

  const treatments = [
    {
      title: "Hemodialysis & Peritoneal Dialysis",
      description: "Advanced dialysis to filter blood when kidneys cannot function.",
    },
    {
      title: "Kidney Biopsy",
      description: "Minimally invasive procedure for accurate diagnosis.",
    },
    {
      title: "Renal Transplant Evaluation",
      description: "Full assessment for kidney transplant candidates.",
    },
    {
      title: "Electrolyte Imbalance Correction",
      description: "Restoring balance of minerals and fluids in the body.",
    },
    {
      title: "Hypertension & Diabetic Kidney Disease Treatment",
      description: "Targeted treatment for chronic condition-related kidney issues.",
    },
    {
      title: "Renal Replacement Therapy",
      description: "Dialysis & transplant-based treatment for kidney failure.",
    },
  ];

  const whyChoose = [
    {
      icon: Clock,
      title: "24x7 Dialysis & Emergency Support",
      description: "Round-the-clock dialysis and emergency renal care.",
    },
    {
      icon: Users,
      title: "Experienced Nephrologists",
      description: "Expert nephrologists and transplant specialists.",
    },
    {
      icon: Award,
      title: "Latest Equipment",
      description: "State-of-the-art technology for precise monitoring.",
    },
    {
      icon: Heart,
      title: "Multidisciplinary Care",
      description: "Integrated diabetic & hypertension care.",
    },
    {
      icon: MapPin,
      title: "Accessible Location",
      description: "Conveniently located near Miyapur & Bachupally.",
    },
  ];

  const expert = {
    name: "Dr. Bhavya Dasara",
    title: "Nephrology",
    designation: "Associate Professor",
    expertise: "Nephrology, Dialysis Management, and Renal Disease Prevention",
    description: "MD, DM, FISN, 13 Years Experience",
    image: BHAVYADASARA,
  }

  const articles = [
    {
      title: "Early Signs of Kidney Problems You Shouldn't Ignore",
      excerpt: "Recognize early kidney disease symptoms and when to seek help.",
    },
    {
      title: "Understanding Dialysis and Its Types",
      excerpt: "A complete guide to hemodialysis and peritoneal dialysis.",
    },
    {
      title: "Diet Tips for Kidney Health",
      excerpt: "Foods that support kidney function and slow progression.",
    },
    {
      title: "Advancements in Kidney Transplant Procedures",
      excerpt: "Latest innovations in kidney transplant care.",
    },
  ];

  const faqs = [
    {
      question: "When should I see a nephrologist?",
      answer:
        "You should see a nephrologist if you have hypertension, diabetes, abnormal tests, swelling, or family history of kidney disease.",
    },
    {
      question: "Do you provide 24x7 dialysis services?",
      answer:
        "Yes, we provide round-the-clock dialysis with expert nephrologists and technicians.",
    },
    {
      question: "Is kidney transplant available at MAMS Hospitals?",
      answer:
        "Yes, we offer complete pre-transplant, surgery coordination, and post-transplant care.",
    },
  ];
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalClinic",
      "name": "MAMS Hospitals Nephrology Department",
      "url": "https://mamshospitals.com/nephrology",
      "logo": "https://mamshospitals.com/MAMS-Logo.png",
      "image": "https://mamshospitals.com/Nephrology.jpg",
      "description": "MAMS Hospitals provides advanced nephrology treatment in Hyderabad including dialysis services, kidney disease treatment, renal transplant care, and hypertension-related kidney management.",
      "medicalSpecialty": "Nephrology",
      "telephone": "+91-7399999981",
      "priceRange": "$$",

      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Bachupally",
        "addressLocality": "Hyderabad",
        "addressRegion": "Telangana",
        "postalCode": "500090",
        "addressCountry": "IN"
      },

      "availableService": [
        {
          "@type": "MedicalProcedure",
          "name": "Dialysis Services"
        },
        {
          "@type": "MedicalProcedure",
          "name": "Kidney Transplant Evaluation"
        },
        {
          "@type": "MedicalProcedure",
          "name": "Chronic Kidney Disease Treatment"
        },
        {
          "@type": "MedicalProcedure",
          "name": "Acute Kidney Injury Management"
        },
        {
          "@type": "MedicalProcedure",
          "name": "Renal Replacement Therapy"
        }
      ],

      "sameAs": [
        "https://www.facebook.com/MAMSHospitals",
        "https://www.instagram.com/mams_hospitals"
      ]
    },

    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Do you provide 24x7 dialysis services?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, MAMS Hospitals provides round-the-clock dialysis services with expert nephrologists and trained dialysis technicians."
          }
        },
        {
          "@type": "Question",
          "name": "Do you offer kidney transplant care?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, MAMS Hospitals offers kidney transplant evaluation, surgery coordination, and post-transplant care."
          }
        },
        {
          "@type": "Question",
          "name": "When should I consult a nephrologist?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You should consult a nephrologist if you have diabetes, hypertension, swelling, abnormal kidney tests, or chronic kidney disease symptoms."
          }
        }
      ]
    },

    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Nephrology Department - MAMS Hospitals",
      "url": "https://mamshospitals.com/nephrology",
      "description": "Best nephrology hospital in Hyderabad offering dialysis, kidney disease treatment, renal transplant care, and chronic kidney disease management.",
      "keywords": [
        "Nephrology Hospital Hyderabad",
        "Best Nephrologist Hyderabad",
        "Dialysis Center Hyderabad",
        "Kidney Specialist Hyderabad",
        "Kidney Failure Treatment Hyderabad",
        "Renal Transplant Hyderabad",
        "Nephrology Hospital Bachupally"
      ]
    }
  ];
  return (
    <>
    <SEO
    title="Best Nephrology Hospital in Hyderabad | Dialysis & Kidney Care | MAMS Hospitals"
    description="MAMS Hospitals offers advanced nephrology treatment in Hyderabad including dialysis services, kidney disease treatment, renal transplant care, and chronic kidney disease management."
    keywords="nephrology hospital Hyderabad,
    best nephrologist Hyderabad,
    dialysis center Hyderabad,
    kidney specialist Hyderabad,
    kidney disease treatment Hyderabad,
    renal transplant Hyderabad,
    kidney failure treatment Hyderabad,
    CKD treatment Hyderabad,
    24x7 dialysis Hyderabad,
    acute kidney injury treatment Hyderabad,
    kidney hospital Bachupally,
    renal care Hyderabad,
    MAMS Hospitals Nephrology"
      schema={schema}
/>

    <div className="flex flex-col min-h-screen">

      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-white py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
              <span className="text-primary font-semibold text-sm">Advanced Kidney Care</span>
            </div>

            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Your Kidneys, Our Priority</h1>

            <p className="text-lg text-gray-700 mb-6">
              MAMS Hospitals offers advanced nephrology care, specializing in acute & chronic kidney disease, dialysis, and transplant.
            </p>

            <p className="text-primary font-semibold text-lg mb-8">
              We protect your kidneys — because healthy kidneys mean a healthier life.
            </p>

           <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
          </div>

          <div className="relative mt-8 md:mt-0">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/Nephrology.jpg"
                alt="Nephrology Department"
                className="w-full h-[300px] md:h-[400px] object-cover"
              canonicalUrl="https://mamshospitals.com/departments/nephrology"
      />
            </div>

            <div className="absolute -bottom-4 left-2 sm:-bottom-6 sm:-left-6 bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-primary/20">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Award className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-bold text-2xl text-primary">24/7</p>
                  <p className="text-sm text-gray-600">Dialysis Services</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SUB-SPECIALTIES */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Sub-Specialties in Nephrology</h2>
            <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
              Comprehensive kidney care covering all aspects of renal health.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((service, index) => (
              <Card
                key={index}
                className="border-apple-gray-100 hover:border-apple-teal transition-all duration-300 hover:shadow-lg rounded-3xl overflow-hidden group"
              >
                <div className="aspect-video relative overflow-hidden bg-apple-gray-50">
                  <img
                    src={service.image || "/placeholder.svg"}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <CardContent className="p-6">
                  {/* <div className="bg-apple-teal/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    {service.icon}
                  </div> */}
                  <h3 className="text-xl font-semibold text-apple-gray-500 mb-2">{service.title}</h3>
                  <p className="text-apple-gray-400 text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* TREATMENTS */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Treatments & Procedures</h2>
            <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
              Evidence-based nephrology treatments using modern technology.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {treatments.map((t, i) => (
              <Card key={i} className="hover:shadow-md transition border-primary/10">
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <div className="bg-primary/10 w-3 h-3 rounded-full mt-2" />

                    <div>
                      <h3 className="font-semibold text-gray-900">{t.title}</h3>
                      <p className="text-gray-600 mt-1">{t.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Why Choose MAMS Hospitals</h2>
            <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
              Leading nephrology care with advanced facilities.
            </p>
          </div>

          {/* First Row */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.slice(0, 3).map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={i} className="text-center border-primary/10 hover:shadow-lg transition">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="font-bold text-xl">{item.title}</h3>
                    <p className="text-gray-600 mt-2">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Second Row */}
          <div className="grid md:grid-cols-2 gap-6 mt-6 max-w-3xl mx-auto">
            {whyChoose.slice(3).map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={i} className="text-center border-primary/10 hover:shadow-lg transition">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="font-bold text-xl">{item.title}</h3>
                    <p className="text-gray-600 mt-2">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

        </div>
      </section>

      {/* SPECIALIST */}
                {doctors.length > 0 && (
          <section className="py-16 px-0 bg-apple-gray-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
              </div>
              <div className="relative">
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                      <DoctorCard doctor={{
                        name: doctor.title.rendered,
                        slug: doctor.slug,
                        imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                        specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                        designation: doctor.acf?.designation || "",
                        qualification: doctor.acf?.qualification || "",
                        experience: doctor.acf?.experience || "",
                      }} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="text-center mt-8">
                <Link to="/doctors">
                  <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">
                    View All Doctors
                  </button>
                </Link>
              </div>
            </div>
          </section>
          )}

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-0 max-w-4xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold">Frequently Asked Questions</h2>
            <p className="text-gray-600 mt-2">Answers to common kidney health concerns.</p>
          </div>

          <Accordion className="space-y-4" type="single" collapsible>
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold hover:text-primary">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department </p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
    </>
  );
}
